import React, { useState, useEffect, useRef } from "react";
import { db, storage, auth } from "../../firebase";
import { signOut, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";

export default function AdminDashboard() {
  const navigate = useNavigate();

  // ✅ Redirect if user not logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/adminlogin");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const [courses, setCourses] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [courseLink, setCourseLink] = useState("");
  const [featured, setFeatured] = useState(false); // ✅ Added state
  const [file, setFile] = useState(null);
  const [editId, setEditId] = useState(null);

  const coursesRef = collection(db, "courses");
  const formRef = useRef(null);

  const fetchCourses = async () => {
    // ✅ Order by creation time (latest first)
    const snapshot = await getDocs(query(coursesRef, orderBy("createdAt", "desc")));
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    // ✅ Featured first
    const sorted = data.sort((a, b) => (b.featured === a.featured ? 0 : b.featured ? 1 : -1));
    setCourses(sorted);
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const uploadImage = async (file) => {
    const fileRef = ref(storage, `courses/${Date.now()}-${file.name}`);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };

  const handleSave = async () => {
    // ✅ Basic Validation
    if (!title.trim() || !description.trim() || !price.trim()) {
      alert("⚠️ Please fill in the course title, description, and price before saving.");
      return;
    }
  
    try {
      let imageUrl = null;
      if (file) {
        imageUrl = await uploadImage(file);
      }
  
      if (editId) {
        const courseDocRef = doc(db, "courses", editId);
        const existingDoc = await getDoc(courseDocRef);
  
        await updateDoc(courseDocRef, {
          title,
          description,
          price,
          courseLink,
          featured,
          ...(imageUrl && { imageUrl }),
          createdAt: existingDoc.exists()
            ? existingDoc.data().createdAt
            : serverTimestamp(),
        });
        setEditId(null);
      } else {
        await addDoc(coursesRef, {
          title,
          description,
          price,
          courseLink,
          featured,
          imageUrl: imageUrl || "",
          createdAt: serverTimestamp(),
        });
      }
  
      // ✅ Reset form
      setTitle("");
      setDescription("");
      setPrice("");
      setCourseLink("");
      setFeatured(false);
      setFile(null);
      fetchCourses();
    } catch (error) {
      console.error("Error saving course:", error);
      alert("❌ Failed to save course: " + error.message);
    }
  };
  
  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "courses", id));
    fetchCourses();
  };

  const handleEdit = (course) => {
    setEditId(course.id);
    setTitle(course.title);
    setDescription(course.description);
    setPrice(course.price);
    setCourseLink(course.courseLink || "");
    setFeatured(course.featured || false); // ✅ Load featured state
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/adminlogin");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <div className="p-6 pt-24 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          BTech Mathematics Course Dashboard
        </h2>
        <button
          onClick={handleLogout}
          className="bg-red-500 text-white px-4 py-2 rounded"
        >
          Logout
        </button>
      </div>

      {/* Course Form */}
      <div ref={formRef} className="bg-white p-5 shadow rounded mb-8">
        <h3 className="text-lg font-semibold mb-3">
          {editId ? "Edit Course" : "Add New Course"}
        </h3>

        <input
          type="text"
          placeholder="Course Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="border p-2 w-full mb-3 rounded"
        />

        <textarea
          placeholder="Course Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="border p-2 w-full mb-3 rounded h-24"
        ></textarea>

        <input
          type="number"
          placeholder="Price (₹)"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="border p-2 w-full mb-3 rounded"
        />

        <input
          type="text"
          placeholder="Course Link (e.g. YouTube, Course Page URL)"
          value={courseLink}
          onChange={(e) => setCourseLink(e.target.value)}
          className="border p-2 w-full mb-3 rounded"
        />

        {/* ✅ Featured Checkbox */}
        <label className="flex items-center gap-2 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={featured}
            onChange={(e) => setFeatured(e.target.checked)}
          />
          <span>Mark as Featured Course</span>
        </label>

        <input
          type="file"
          onChange={(e) => setFile(e.target.files[0])}
          className="mb-3"
        />

        <button
          onClick={handleSave}
          className="bg-green-600 text-white px-4 py-2 rounded"
        >
          {editId ? "Update Course" : "Add Course"}
        </button>
      </div>

      {/* Course List */}
      <div>
        {courses.map((course) => (
          <div key={course.id} className="bg-white shadow p-4 mb-4 rounded relative">
            {course.featured && (
              <span className="absolute top-2 right-3 bg-yellow-400 text-xs font-semibold px-2 py-1 rounded">
                🌟 Featured
              </span>
            )}
            {course.imageUrl && (
              <img
                src={course.imageUrl}
                alt={course.title}
                className="w-auto h-48 object-cover mb-3 rounded"
              />
            )}
            <h3 className="text-xl font-bold text-gray-800">{course.title}</h3>
            <p className="text-gray-600 mb-2">{course.description}</p>
            <p className="font-semibold text-[#f97316] text-lg mb-2">
              ₹ {course.price}
            </p>

            {/* ✅ Show View / Enroll button */}
            {course.courseLink ? (
              <a
                href={(() => {
                  let link = course.courseLink.trim();
                  link = link
                    .replace(/^http\/\/:/i, "http://")
                    .replace(/^https\/\/:/i, "https://");
                  if (!/^https?:\/\//i.test(link)) link = `https://${link}`;
                  return link;
                })()}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline mb-3 inline-block"
              >
                View Course
              </a>
            ) : (
              <button className="text-blue-600 underline mb-3 inline-block">
                Enroll now
              </button>
            )}

            <div className="flex gap-3 mt-2">
              <button
                onClick={() => handleEdit(course)}
                className="bg-yellow-500 text-white px-3 py-1 rounded"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(course.id)}
                className="bg-red-500 text-white px-3 py-1 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
